<script setup>
  import ArrowRight from '@/assets/icons/sidebar/arrow-right.svg'
  import { useI18n } from 'vue-i18n'

  import { ref } from 'vue'

  import SidebarPanelIcon from '@/features/layout/components/SidebarPanelIcon.vue'
  import HamburgerMenuMobileGroupLink from '@/features/layout/components/hamburgerMobile/HamburgerMenuMobileGroupLink.vue'
  import { useLayoutStore } from '@/features/layout/useLayoutStore.js'

  const props = defineProps(['item'])
  const layoutStore = useLayoutStore()
  const { to, icon, children, label, iconClass } = props.item
  const isOpened = ref(false)

  const { t } = useI18n()

  function handleToggle() {
    isOpened.value = !isOpened.value
  }

  const As = to ? 'RouterLink' : 'span'

  function handleClickMenuItem() {
    if (!children?.length) {
      layoutStore.closeSidebar()
    }
  }
</script>

<template>
  <li class="group-item">
    <component
      :is="As"
      class="group-link"
      :to="to"
      :class="{
        centered: !layoutStore.sidebarOpened,
      }"
      @click="handleClickMenuItem"
    >
      <span class="group-icon">
        <SidebarPanelIcon :icon="icon" :icon-class="iconClass" />
      </span>
      <template v-if="layoutStore.sidebarOpened">
        <div class="group-item-name">{{ t(label) }}</div>
        <span
          v-if="children?.length"
          class="toggle"
          :class="{ openedGroup: isOpened }"
          @click.prevent="handleToggle"
        >
          <ArrowRight />
        </span>
      </template>
    </component>

    <ul v-if="isOpened" class="group-item-children">
      <HamburgerMenuMobileGroupLink
        v-for="subItem of children"
        :key="subItem.to || subItem.label"
        :to="subItem.to"
        :icon="subItem.icon"
        :icon-class="subItem.iconClass"
        >{{ t(subItem.label) }}</HamburgerMenuMobileGroupLink
      >
    </ul>
  </li>
</template>

<style lang="scss" scoped>
  .group-item {
    display: flex;
    flex-direction: column;
    padding: 0;
    margin: 0;
    list-style: none;
    position: relative;

    ul {
      list-style: none;
      padding: 0;
      margin: 0;
    }
  }

  .group-link {
    display: flex;
    padding: 0 16px;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    color: var(--fm-ui-hamburger-group-link-color);
    height: 45px;
    border-left: 6px solid transparent;

    &.centered {
      justify-content: center;
    }

    &.router-link-active {
      background: var(--fm-ui-hamburger-group-link-active-background-color);
      border-color: var(--fm-ui-hamburger-group-link-active-border-color);
    }

    &:hover {
      background: var(--fm-ui-hamburger-group-link-hover-background-color);
      color: var(--fm-ui-hamburger-group-link-hover-color);
    }

    &:focus {
      background: var(--fm-ui-hamburger-group-link-focus-background-color);
      color: var(--fm-ui-hamburger-group-link-focus-color);
    }

    &:focus-within {
      background: var(--fm-ui-hamburger-group-link-focus-background-color);
      color: var(--fm-ui-hamburger-group-link-focus-color);
    }
  }

  .group-icon {
    width: 16px;
    height: 16px;
  }

  .toggle {
    width: 30px;
    height: 100%;
    display: flex;
    cursor: pointer;
    align-items: center;
    justify-content: center;
    margin-left: auto;

    &.openedGroup {
      transform: rotate(90deg);
    }
  }

  .group-item-children {
    display: flex;
    flex-direction: column;
    gap: 4px;
    background: var(--fm-ui-hamburger-group-children-background-color);
  }
</style>
